import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import type { ListState } from './syncStates';
import type { Finding } from './Findings';

export const FILTER_KEYS = [
  'student_id', 'staffer_id', 'subscriber_id', 'assignment_id', 'classroom_id',
] as const;

export type FilterKey = typeof FILTER_KEYS[number];
export type SyncFilters = Partial<Record<FilterKey, string>>;

export interface SyncRow {
  assignment_id: number;
  assignment_title: string;
  student_id: number;
  student_name: string | null;
  classroom: string | null;
  teacher: string | null;
  teacher_id: number | null;
  subscriber: string | null;
  platform: string | null;
  grading: string;
  current_score: number | null;
  synced_score: number | null;
  synced_at: string | null;
  attempts: number;
  seconds_to_sync: number | null;
  state: ListState;
}

interface SyncRowsPayload {
  rows: SyncRow[];
  scoped: boolean;
  /** Keyed by assignment id — why that assignment isn't delivering. */
  diagnostics: Record<string, Finding[]>;
}

/** Filters are the scope: with none set the query would be every score on the platform. */
export const hasScope = (filters: SyncFilters) => (
  FILTER_KEYS.some(key => (filters[key] ?? '').trim().length > 0)
);

const useSyncRows = (filters: SyncFilters, enabled = true) => useQuery({
  enabled: enabled && hasScope(filters),
  queryFn: async (): Promise<SyncRowsPayload> => {
    const { data } = await axios.get(Routes.plato_api_sysadmin_grade_sync_path(), {
      params: filters,
    });

    return data.data;
  },
  queryKey: ['gradeSyncRows', filters],
});

export default useSyncRows;
